type StageConfig = {
  authority: string;
  client_id: string;
  redirect_uri: string;
  identity_provider: string;
};

type AuthConfig = {
  authority: string;
  client_id: string;
  redirect_uri: string;
  response_type: string;
  scope: string;
  identity_provider: string;
  metadata: {
    authorization_endpoint: string;
    token_endpoint: string;
    end_session_endpoint: string;
  };
  extraQueryParams?: {
    login_hint: string;
  };
};

/**
 * Configuration mapping for different deployment environments/stages based on hostnames.
 * Each entry contains authentication settings for a specific environment:
 * - authority: Cognito authentication endpoint URL
 * - client_id: OAuth client ID for the application
 * - redirect_uri: URL to redirect to after successful authentication
 * - identity_provider: SSO identity provider ID
 */
const HOSTNAME_CONFIG: Record<string, StageConfig> = {
  'beta.eu.learning.warehousejobs.in': {
    authority: 'https://learnings-beta-eu.auth.eu-west-1.amazoncognito.com',
    client_id: '733maj9p2is7aeku48tgv3uunk',
    redirect_uri: 'https://beta.eu.learning.warehousejobs.in',
    identity_provider: 'hvh-learning-sso-idp-beta-eu',
  },
  'gamma.eu.learning.warehousejobs.in': {
    authority: 'https://learnings-gamma-eu.auth.eu-west-1.amazoncognito.com',
    client_id: '7sje1j7g3csr75ae3r1rls3e6',
    redirect_uri: 'https://gamma.eu.learning.warehousejobs.in',
    identity_provider: 'hvh-learning-sso-idp-gamma-eu',
  },
  'learning.warehousejobs.in': {
    authority: 'https://learnings-prod-eu.auth.eu-west-1.amazoncognito.com',
    client_id: '5k8d0cr5q5gkuonr3d2pi6c2q9',
    redirect_uri: 'https://learning.warehousejobs.in',
    identity_provider: 'hvh-learning-sso-idp-prod-eu',
  },
  // Beta test values
  localhost: {
    authority: 'https://learnings-beta-eu.auth.eu-west-1.amazoncognito.com',
    client_id: '733maj9p2is7aeku48tgv3uunk',
    redirect_uri: 'http://localhost:3000',
    identity_provider: 'hvh-learning-sso-idp-beta-eu',
  },
};

export const getAuthConfigFromHostname = (): StageConfig | null => {
  const hostname = window.location.hostname;
  return HOSTNAME_CONFIG[hostname] || null;
};

export const createAuthConfig = (): AuthConfig | null => {
  const currentUrl = new URL(window.location.href);
  const loginHint = new URLSearchParams(currentUrl.search).get('login_hint');

  const stageConfig = getAuthConfigFromHostname();

  if (currentUrl.pathname !== '/' && currentUrl.search) {
    sessionStorage.setItem('post_auth_path', currentUrl.pathname);
    sessionStorage.setItem('post_auth_params', currentUrl.search);
  }

  if (!stageConfig) {
    return null;
  }

  return {
    authority: stageConfig.authority,
    client_id: stageConfig.client_id,
    redirect_uri: stageConfig.redirect_uri,
    response_type: 'code',
    scope: 'email openid profile',
    identity_provider: stageConfig.identity_provider,
    metadata: {
      authorization_endpoint: `${stageConfig.authority}/oauth2/authorize`,
      token_endpoint: `${stageConfig.authority}/oauth2/token`,
      end_session_endpoint: `${stageConfig.authority}/logout`,
    },
    ...(loginHint && {
      extraQueryParams: {
        login_hint: loginHint,
      },
    }),
  };
};
